import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import styles from '../component-styles/modal.module.scss'
import { Preloader } from './loader'

interface MProps {
  children?: React.ReactNode
  active?: boolean
  narrow?: boolean
  superNarrow?: boolean
  superWide?: boolean
  toggleActive: () => void
  className?: string
  type?: 'normal' | 'white' | 'noHeader' | 'column' | 'green'
  loading?: boolean
  isWarning?: boolean
}

export default function Modal({
  children,
  active = false,
  narrow = false,
  superNarrow = false,
  superWide = false,
  toggleActive,
  className = '',
  type = 'normal',
  loading = false,
  isWarning = false,
}: MProps): React.ReactElement | null {
  const [transitionActive, setTransition] = useState(active)

  useEffect((): void => {
    if (!active) {
      setTimeout((): void => setTransition(active), 500)
    } else {
      setTransition(active)
    }
  }, [active])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.keyCode === 27) {
        toggleActive()
      }
    }
    if (transitionActive) {
      document.addEventListener('keydown', onKeyDown)
    } else {
      document.removeEventListener('keydown', onKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line
  }, [transitionActive])

  const root = document.getElementById('modals')

  if (!root) return null

  const modalClassName = classNames(styles.modal, {
    [styles.narrowModal]: narrow,
    [styles.superNarrowModal]: superNarrow,
    [styles.superWideModal]: superWide,
    [styles.noHeaderModal]: type === 'noHeader',
    [styles.whiteModal]: type === 'white',
    [styles.greenModal]: type === 'green',
    [styles.columnModal]: type === 'column',
    [styles.warningModal]: isWarning,
  })
  const modalWrapperClassName = classNames({
    [styles.darkBox]: active,
    [styles.hideBox]: !active,
    [styles.loading]: loading,
  })
  // onClick={toggleActive} role="presentation"
  return createPortal(
    <div className={modalWrapperClassName} aria-label="modal">
      <style>
        {transitionActive
          ? `
            #root, body {
              overflow: hidden !important;
            }
          `
          : ''}
      </style>
      <section className={modalClassName}>
        <div className={`${styles.inner} ${className}`}>
          <a
            className={styles.close}
            onClick={(e) => {
              e.preventDefault()
              toggleActive()
            }}
            href="#close"
          >
            Close
          </a>
          {children}
        </div>
        {loading && (
          <div className={styles.preloader}>
            <Preloader style={{ height: 50, width: 70 }} />
          </div>
        )}
      </section>
    </div>,
    root,
  )
}

// #root * {
//   overflow: hidden !important;
// }

interface Props {
  children?: React.ReactNode
  style?: React.CSSProperties
}

export function ModalHead({ children, style }: Props): React.ReactElement {
  return (
    <div className={styles.head} style={style}>
      {children}
    </div>
  )
}

interface PropsModalBody {
  children?: React.ReactNode
  noPadding?: boolean
  className?: string
}

export function ModalBody({
  children,
  noPadding,
  className,
}: PropsModalBody): React.ReactElement {
  return (
    <div
      className={classNames(className, styles.body, {
        [styles.noPadding]: noPadding,
      })}
    >
      {children}
    </div>
  )
}

export function ModalFooter({ children }: Props): React.ReactElement {
  return <div className={styles.footer}>{children}</div>
}
